import { useMediaQuery, useTheme } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import OptionsButtonCustom from "../../../../components/option-button/OptionsButtonCustom";
import TableRowCommon from "../../../../components/table/TableRowCommon";
import {
  setLoading,
  unsetLoading,
} from "../../../../store/loading/loading.slice";
import ContainerLayout from "../../../../components/container-layout/ContainerLayout";
import StateComponents from "../../../../components/state-component/StateComponents";
import { debouncedApiCall } from "../../../../utils/app.utils";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { ButtonPrimary } from "../../../../components/button/ButtonCustoms";
import TableCustom from "../../../../components/table/tableCustom";
import ModalCustom from "../../../../components/modal/ModalCustom";
import NavigateListCustom from "../../../../components/navigation/NavigationListCustom";
import { routeName } from "../../../../routes/routeName";
import AccordionUpload from "../../../../components/accordion/AccordionUpload";
import ImageSlider from "../../../../components/slider/slider-images";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { edpexProjectAPI } from "../../../../api/edpex-project/edpex-project.api";
import { dateTimeToView, dateToView } from "../../../../utils/date.utils";
import { alertError } from "../../../../components/alert/swal";
import { edpexFormMasterAPI } from "../../../../api/edpex-form-master/edpex-form-master.api";
import StatusCustom from "../../../../components/status/statusCustom";
import { ed_pex_project_status_data } from "../enum/enum";
import { YearsApi } from "../../../../api/yeas/years.api";
import moment from "moment";
import { RootState } from "../../../../store/store";

interface UploadedFile {
  id: number;
  name: string;
  size: string;
  uploadDate: string;
  file: File;
}

const EdpexAddProject = () => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate: NavigateFunction = useNavigate();

  const base_url = useSelector((state: RootState) => state.api.image_api);
  // console.log(base_url,'base_url')

  const [showModal, setShowModal] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageLimit, setPageLimit] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(13);
  const [sortBy, setSortBy] = useState<string>("modified_date");
  const [sortType, setSortType] = useState<string>("DESC");
  const [search, setSearch] = useState<string>("");
  const [data, setData] = useState<any[]>([]);
  const [start_date, set_start_date] = useState<string>("");
  const [end_date, set_end_date] = useState<string>("");
  const [status, set_status] = useState<any>(null);
  //
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [multiUpload, setMultiImage] = useState<any[]>([]);



  const handleChangeDateRange = (st: string, end: string) => {
    set_start_date(st);
    set_end_date(end)
  }
  const handleGetDataPopUp = useCallback(async () => {
    try {
      dispatch(setLoading());

      const res = await edpexFormMasterAPI.getEdpexFormMasterByType({
        form_type: "EXAMPLE_PROJECT",
        is_release: 1,
      });

      if (res.data?.status < 300 && res.data?.data) {
        const {
          document_files = [],
          image_files = [],
        } = res.data.data;
        // const baseURL = "https://ibscapi.kivaru.com/";

        const mapFileData = (
          files: any[],
          type: "FILE_IMAGE" | "FILE_DOCUMENT"
        ): UploadedFile[] =>
          files.map((file) => ({
            id: file.id_edpex_form_file,
            name: file.file_name,
            size: file.file_size
              ? `${(file.file_size / 1024).toFixed(2)} KB`
              : "Unknown",
            uploadDate: file.modified_date || file.created_date || "",
            file_path: file.file_path,
            fullUrl: `${base_url}${file.file_path}${encodeURIComponent(
              file.file_name
            )}`,
            type,
            file: null as unknown as File, // Placeholder for UI compatibility
          }));

        setUploadedFiles([...mapFileData(document_files, "FILE_DOCUMENT")]);
        setMultiImage([...mapFileData(image_files, "FILE_IMAGE")]);
      }
    } catch (error: any) {
      console.error("Get data error:", error);
      alertError(t("เกิดข้อผิดพลาด"), t("โหลดข้อมูลไม่สำเร็จ"));
    } finally {
      dispatch(unsetLoading());
    }
  }, [dispatch, t]);

  useEffect(() => {
    handleGetDataPopUp();
  }, [handleGetDataPopUp]);

  const handleRequestSort = (sortBy: string, sortType: string) => {
    setSortBy(sortBy);
    setSortType(sortType);
  };

  const headCells = [
    {
      id: "ปีงบประมาณ",
      width: 100,
      sortable: true,
      disablePadding: false,
      align: "center",
      label: t("ปีงบประมาณ"),
    },
    {
      id: "ชื่อโครงการ",
      disablePadding: false,
      align: "center",
      width: 141,
      label: t("ชื่อโครงการ"),
    },
    {
      id: "ส่วนงานที่รับผิดชอบ",
      disablePadding: false,
      align: "center",
      width: 257,
      label: t("ส่วนงานที่รับผิดชอบ"),
    },
    {
      id: "ระยะเวลาดำเนินการ",
      disablePadding: false,
      align: "center",
      width: 257,
      label: t("ระยะเวลาดำเนินการ"),
    },
    {
      id: "งบประมาณ",
      width: 100,
      // sortable: true,
      disablePadding: false,
      align: "center",
      label: t("งบประมาณ"),
    },
    {
      id: "ผู้ที่สร้าง",
      disablePadding: false,
      align: "center",
      width: 141,
      label: t("ผู้ที่สร้าง/ปรับปรุงล่าสุด"),
    },
    {
      id: "วันที่สร้าง",
      disablePadding: false,
      align: "center",
      width: 257,
      label: t("วันที่สร้าง/ปรับปรุงล่าสุด"),
    },
    {
      id: "สถานะ",
      disablePadding: false,
      align: "center",
      width: 257,
      label: t("สถานะ"),
    },
    {
      id: "s",
      disablePadding: false,
      align: "center",
      width: 100,
      class: matches,
      label: "",
    },
  ];

  const renderData = (objData: any, no: any) => {
    no = page * pageLimit - pageLimit + no + 1;
    const {
      id_edpex_project,
      fiscal_year,
      department_name_th,
      edpex_project_start_date,
      edpex_project_end_date,
      edpex_project_budget,
      modified_name,
      modified_date,
      edpex_project_status,
      edpex_project_name
    } = objData;

    const objRenderData = {
      key: no,
      id: no,
      obj: objData,
      columns: [
        {
          option: "TEXT",
          align: "left",
          label: fiscal_year ? + fiscal_year : "",
        },
        {
          option: "TEXT",
          align: "center",
          label: edpex_project_name ?? "-",
        },
        {
          option: "TEXT",
          align: "center",
          label: department_name_th || "-",
        },
        {
          option: "TEXT",
          align: "center",
          label:
            edpex_project_start_date && edpex_project_end_date
              ? dateToView(edpex_project_start_date ?? null) +
              " - " +
              dateToView(edpex_project_end_date ?? null)
              : "-",
        },
        {
          option: "PRICE",
          align: "center",
          label: edpex_project_budget || "0",
        },
        {
          option: "TEXT",
          align: "center",
          label: modified_name || "-",
        },
        {
          option: "TEXT",
          align: "center",
          label: modified_date ? dateTimeToView(modified_date) : "-",
        },
        {
          option: "TEXT",
          align: "left",
          label: <StatusCustom
            sx={{ maxWidth: 'fit-content', px: 2.5 }}
            text={ed_pex_project_status_data?.find((x) => x.value === edpex_project_status)?.label || "-"}
            type={ed_pex_project_status_data?.find((x) => x.value === edpex_project_status)?.label || "-"}
          />
        },
        {
          option: "COMPONENT",
          align: "center",
          class: matches,
          width: 300,
          component: (
            <OptionsButtonCustom
              enableView
              enableEdit
              onEditClick={() => {
                navigate(
                  routeName.cms.edpexAddProjectDetail.replace(
                    ":id",
                    id_edpex_project
                  ) + "?project-type=add-project"
                );
              }}
              onViewClick={() => {
                navigate(
                  routeName.cms.edpexAddProjectDetail.replace(
                    ":id",
                    id_edpex_project
                  ) + "?mode=VIEW&&project-type=add-project"
                );
              }}
            />
          ),
        },
      ],
    };
    return <TableRowCommon {...objRenderData} />;
  };

  const [id_fiscal_year, set_id_fiscal_year] = useState<any>(null);
  const [fiscal_year_data, set_fiscal_year_data] = useState<any[]>([]);

  const handleGetFiscalYear = async () => {
    await YearsApi.getYears().then((res) => {
      if (res.status < 300) {
        const response = res.data?.data || [];
        set_fiscal_year_data(response);
      }
    }).catch((er) => {
      console.log(er)
    })
  }

  const handleGetData = useCallback(async () => {
    let query: any = {
      page: page,
      limit: pageLimit,
    };
    if (search) query = { ...query, keyword: search };
    if (start_date) query.start_date = moment(start_date).format("YYYY-MM-DD");
    if (end_date) query.end_date = moment(end_date).format("YYYY-MM-DD");
    if (id_fiscal_year) query.fiscal_year = id_fiscal_year?.year;
    if (status) query.status = status?.value
    dispatch(setLoading());
    await edpexProjectAPI
      .getEdpexProjectList(query)
      .then((res) => {
        if (res.status < 300) {
          setData(res.data?.data?.items || []);
          setTotalItems(res.data?.data?.meta?.totalItems || 0);
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        dispatch(unsetLoading());
      });
  }, [page, pageLimit, search, end_date, start_date, id_fiscal_year, status]);

  useEffect(() => {
    handleGetData();
  }, [handleGetData]);

  useEffect(() => {
    handleGetFiscalYear();
  }, [])

  return (
    <>
      <ContainerLayout
        title={t("เพิ่มโครงการ/กิจกรรม/งาน")}
        navigateContent={
          <NavigateListCustom
            navigateData={[
              {
                key: "1",
                name: t("ระบบ Edpex"),
                disabled_match: true,
                pathName: ""
              },
              {
                key: "2",
                name: t("เพิ่มโครงการ/กิจกรรม/งาน"),
                pathName: routeName.cms.edpexAddProject,
              },
            ]}
          />
        }
        content={
          <>
            <div className="w-full flex flex-col xl:flex-row justify-between gap-4">
              <span className="flex-1">
                <StateComponents
                  otherProps={{ spacing: 2 }}
                  typeProps={[
                    {
                      seq: 1,
                      type: "TEXT_FIELD",
                      gridProps: {
                        xs: 12,
                        xl: 2.5,
                      },
                      fieldProps: {
                        startAdornment: <SearchOutlinedIcon />,
                        placeholder: t(
                          "ค้นหา ชื่อ "
                        ),
                        onChange: (e) =>
                          debouncedApiCall({
                            searchItem: e.target.value,
                            setItem: setSearch,
                          }),
                      },
                    },
                    {
                      seq: 2,
                      type: "AUTO-COMPLETE",
                      gridProps: {
                        xs: 12,
                        xl: 2.5,
                      },
                      fieldProps: {
                        getOptionLabel: (op) => op?.year || "",
                        labelId: "ss",
                        placeholder: "ทุกปีงบประมาณ",
                        onChange: (_, val) => set_id_fiscal_year(val ?? null),
                        options: fiscal_year_data,
                        value: id_fiscal_year || null,
                      },
                    },
                    {
                      seq: 3,
                      type: "DATE_RANGE2",
                      gridProps: {
                        xs: 12,
                        xl: 2.5,
                      },
                      fieldProps: {
                        handleChange: (st, end) => {
                          handleChangeDateRange(st ?? "", end ?? "")
                        },
                        placeholder: "ช่วงวันที่",
                        value: {
                          end_date,
                          start_date
                        },
                      },
                    },
                    {
                      seq: 4,
                      type: "AUTO-COMPLETE",
                      gridProps: {
                        xs: 12,
                        xl: 2.5,
                      },
                      fieldProps: {
                        labelId: "AA",
                        placeholder: "สถานะ",
                        onChange: (_, val) => set_status(val ?? null),
                        getOptionLabel: (op) => op?.label,
                        options: ed_pex_project_status_data,
                        value: status || null,
                      },
                    },
                  ]}
                />
              </span>
              <span>
                <ButtonPrimary
                  mode="add"
                  onClick={() => {
                    setShowModal(true);
                  }}
                  buttonName={t("เพิ่มโครงการ/กิจกรรม/งาน")}
                />
              </span>
            </div>
            <div className="mt-10">
              <section className="w-full mt-10">
                <TableCustom
                  tableMinWidth={1920}
                  tableFixedWidth
                  customScroll
                  page={page}
                  pageLimit={pageLimit}
                  sortBy={sortBy}
                  sortType={sortType}
                  headCells={headCells}
                  rowCount={totalItems}
                  rowsData={data?.map((data: any, i: any) => {
                    return renderData(data, i);
                  })}
                  onSort={handleRequestSort}
                  setPageLimit={(value: number) => setPageLimit(value)}
                  setPage={(value: number) => setPage(value)}
                />
              </section>
            </div>
          </>
        }
      />

      {showModal && (
        <ModalCustom
          open
          title={"ตัวอย่างแบบฟอร์มต่างๆ ที่เกี่ยวข้องกับระบบ Edpex"}
          subTitle={t("")}
          onClose={() => {
            setShowModal(false);
          }}
          component={
            <div className="w-full">
              <ImageSlider slides={multiUpload} />

              <AccordionUpload
                defaultExpanded
                required
                enableButtonLeft
                titleName="ไฟล์แนบ"
                uploadedFiles={uploadedFiles}
                setUploadedFiles={setUploadedFiles}
                acceptedFileTypes=".pdf,.doc,.docx,.jpg,.png,.xlsx"
                maxFileSize={5} // 5MB limit
                multiple={true}
                isView={true}
              />

              <div className="mt-6 w-full flex justify-center gap-4 items-center">
                <span>
                  <ButtonPrimary
                    mode="cancel"
                    buttonName={t("BUTTON.CANCEL")}
                    onClick={() => {
                      setShowModal(false);
                    }}
                  />
                </span>
                <span>
                  <ButtonPrimary
                    mode="next"
                    buttonName={t("ยืนยัน")}
                    onClick={() => {
                      navigate(routeName.cms.edpexAddProjectCreate + "?project-type=add-project");
                    }}
                  />
                </span>
              </div>
            </div>
          }
        />
      )}
    </>
  );
};

export default EdpexAddProject;
